/**
 * nav-config.ts
 * Shared Zod contracts for nav-config feature — NAV-4
 *
 * Covers:
 *  - NavConfigRowSchema: DB read shape (no company_id — tenant from context)
 *  - NavConfigUpsertSchema: single PUT entry validation
 *  - NavConfigUpsertBatchSchema: full PUT body
 *  - ResolvedNavItemSchema: GET /nav/config response item
 *  - NavConfigResponseSchema: GET /nav/config response wrapper { items: [] }
 *  - NavConfigByModuleSchema: GET/PUT /platform/companies/:id/nav-config response
 *    (persisted override rows grouped by moduleKey). PUT request body is a bare
 *    NavConfigUpsertBatch array — NOT { companyId, entries }.
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 * company_id is NEVER in any contract (KC-3.5 — always from context).
 *
 * Spec: NAV-4 / design §§1-5
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// NavConfigRowSchema — shape of a DB row returned by getNavConfig (NAV-3.1)
// Used for reading; no company_id.
// ---------------------------------------------------------------------------

export const NavConfigRowSchema = z.object({
  itemKey: z.string().min(1),
  visible: z.boolean(),
  position: z.number().int(),
  labelOverride: z.string().nullable(),
});

export type NavConfigRow = z.infer<typeof NavConfigRowSchema>;

// ---------------------------------------------------------------------------
// NavConfigUpsertSchema — single entry in a PUT /nav-config batch (NAV-7.1)
// company_id is NOT here — it comes from the URL param (:id) on the backend.
// ---------------------------------------------------------------------------

export const NavConfigUpsertSchema = z.object({
  moduleKey: z.string().min(1),
  itemKey: z.string().min(1), // NAV-4-A: empty string is rejected
  visible: z.boolean(),
  position: z.number().int(),
  labelOverride: z.string().optional(), // optional; omitted = no override
});

export type NavConfigUpsertEntry = z.infer<typeof NavConfigUpsertSchema>;

// ---------------------------------------------------------------------------
// NavConfigUpsertBatchSchema — the full array used in the PUT body (NAV-7.1)
// ---------------------------------------------------------------------------

export const NavConfigUpsertBatchSchema = z.array(NavConfigUpsertSchema);

export type NavConfigUpsertBatch = z.infer<typeof NavConfigUpsertBatchSchema>;

// ---------------------------------------------------------------------------
// ResolvedNavItemSchema — item in GET /nav/config response (NAV-4.1, NAV-5)
// label already reflects label_override when present.
// route is included so the frontend can navigate without rejoining the manifest.
// ---------------------------------------------------------------------------

/**
 * Explicit interface (rather than z.infer) so `requiredCapability` accepts a
 * readonly string[] — NAV_REGISTRY's items (packages/contracts-kernel/src/
 * nav-registry.ts) are declared `as const`, which makes their
 * requiredCapability arrays readonly tuples. z.array(z.string()) alone would
 * infer a mutable `string[]`, rejecting that readonly value at the
 * nav-config.routes.ts call site that copies regItem.requiredCapability
 * straight through. Matches NavItem.requiredCapability's own type in
 * nav-manifest.ts.
 */
export interface ResolvedNavItem {
  itemKey: string;
  moduleKey: string;
  label: string;
  route: string;
  // any-of (2026-08-31-requi-aprobacion-oc-por-monto, design.md D7): a string
  // or an array of capability strings — the caller needs only one. See
  // NavItem.requiredCapability in nav-manifest.ts for the full rationale.
  requiredCapability?: string | readonly string[] | undefined;
  position: number;
}

export const ResolvedNavItemSchema: z.ZodType<ResolvedNavItem> = z.object({
  itemKey: z.string().min(1),
  moduleKey: z.string().min(1),
  label: z.string().min(1), // manifest label or label_override — already resolved
  route: z.string(),
  requiredCapability: z.union([z.string(), z.array(z.string())]).optional(),
  position: z.number().int(),
});

// ---------------------------------------------------------------------------
// NavConfigResponseSchema — GET /nav/config wrapped response (NAV-5)
// WRAPPED as { items: [...] } — NOT a bare array.
// ---------------------------------------------------------------------------

export const NavConfigResponseSchema = z.object({
  items: z.array(ResolvedNavItemSchema),
});

export type NavConfigResponse = z.infer<typeof NavConfigResponseSchema>;

// ---------------------------------------------------------------------------
// NavConfigByModuleSchema — GET and PUT /platform/companies/:id/nav-config response
// The persisted override rows for one company, grouped by moduleKey:
//   { [moduleKey]: NavConfigRow[] }   ( {} when the company has no overrides ).
// NOTE: the PUT REQUEST body is a bare NavConfigUpsertBatch array (see above),
// NOT a { companyId, entries } wrapper — companyId comes from the :id URL param.
// ---------------------------------------------------------------------------

export const NavConfigByModuleSchema = z.record(z.string(), z.array(NavConfigRowSchema));

export type NavConfigByModule = z.infer<typeof NavConfigByModuleSchema>;
